import { useState, useEffect, useRef, useCallback } from 'react'
import { useUIv2 } from '@/hooks/useUIv2'
import { ReviewTabLegacy } from './ReviewTabLegacy'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Save, X, ChevronUp, ChevronDown } from 'lucide-react'
import { markdownComponents } from '@/config/markdownConfig'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { apiService } from '@/service/api'
import { useTranslation } from '@/i18n'

export interface ReviewTabProps {
  pageText: string | null
  docId?: string
  currentPage?: number
  onContentUpdate?: (newContent: string, pageNumber: number) => void
  isEditing?: boolean
  onEditingChange?: (editing: boolean) => void
  searchQuery?: string
  /**
   * Optional save override. When provided, the edit is persisted via this
   * callback instead of the default per-page OCR text endpoint. FS-557 uses it
   * so a transcript edit writes the whole markdown body (documents.location +
   * KG layout.md + re-ingest) rather than the page-text store.
   */
  onSave?: (newContent: string) => Promise<void>
}

function ReviewTabV2({
  pageText,
  docId,
  currentPage,
  onContentUpdate,
  isEditing: externalIsEditing,
  onEditingChange,
  searchQuery,
  onSave,
}: ReviewTabProps) {
  const { t } = useTranslation()
  const [internalIsEditing, setInternalIsEditing] = useState(false)
  const isEditing = externalIsEditing ?? internalIsEditing
  const setIsEditing = onEditingChange ?? setInternalIsEditing

  const [editedContent, setEditedContent] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const [matchCount, setMatchCount] = useState(0)
  const [activeMatch, setActiveMatch] = useState(0)

  useEffect(() => {
    if (isEditing) {
      setIsEditing(false)
      setEditedContent('')
    }
  }, [currentPage])

  useEffect(() => {
    if (isEditing) setEditedContent(pageText || '')
  }, [isEditing])

  // ── Search highlighting via DOM after markdown renders ──────
  const highlightMatches = useCallback(() => {
    const container = contentRef.current
    if (!container) return

    // Clear existing highlights
    container.querySelectorAll('mark[data-search-highlight]').forEach(mark => {
      const parent = mark.parentNode
      if (parent) {
        parent.replaceChild(document.createTextNode(mark.textContent || ''), mark)
        parent.normalize()
      }
    })

    if (!searchQuery || searchQuery.length < 2) {
      setMatchCount(0)
      setActiveMatch(0)
      return
    }

    const query = searchQuery.toLowerCase()
    const marks: HTMLElement[] = []

    // Walk all text nodes and wrap matches
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
    const textNodes: Text[] = []
    while (walker.nextNode()) textNodes.push(walker.currentNode as Text)

    for (const node of textNodes) {
      const text = node.textContent || ''
      const lower = text.toLowerCase()
      let idx = lower.indexOf(query)
      if (idx === -1) continue

      const frag = document.createDocumentFragment()
      let lastIdx = 0

      while (idx !== -1) {
        // Text before match
        if (idx > lastIdx) frag.appendChild(document.createTextNode(text.slice(lastIdx, idx)))
        // Highlighted match
        const mark = document.createElement('mark')
        mark.setAttribute('data-search-highlight', 'true')
        mark.className = 'bg-yellow-200 dark:bg-yellow-700/50 rounded-sm px-0.5'
        mark.textContent = text.slice(idx, idx + searchQuery.length)
        frag.appendChild(mark)
        marks.push(mark)

        lastIdx = idx + searchQuery.length
        idx = lower.indexOf(query, lastIdx)
      }

      // Remaining text
      if (lastIdx < text.length) frag.appendChild(document.createTextNode(text.slice(lastIdx)))

      node.parentNode?.replaceChild(frag, node)
    }

    setMatchCount(marks.length)
    // Scroll to first match
    if (marks.length > 0) {
      setActiveMatch(1)
      marks[0].classList.add('bg-orange-300', 'dark:bg-orange-600/60')
      marks[0].scrollIntoView({ block: 'center', behavior: 'smooth' })
    } else {
      setActiveMatch(0)
    }
  }, [searchQuery])

  // ── Mark [Unidentified] text red with hover tooltip ─────────
  const markUnidentified = useCallback(() => {
    const container = contentRef.current
    if (!container) return

    // Clean up previous markers
    container.querySelectorAll('[data-unidentified]').forEach(el => {
      const text = document.createTextNode(el.textContent || '')
      el.parentNode?.replaceChild(text, el)
    })
    container.normalize()

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
    const textNodes: Text[] = []
    while (walker.nextNode()) textNodes.push(walker.currentNode as Text)

    const pattern = /\[Unidentified\]|\[Undefined\]|\bUndefined\b|\bUnidentified\b/gi

    for (const node of textNodes) {
      const text = node.textContent || ''
      if (!pattern.test(text)) continue
      pattern.lastIndex = 0

      const frag = document.createDocumentFragment()
      let lastIdx = 0
      let match: RegExpExecArray | null

      while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIdx) {
          frag.appendChild(document.createTextNode(text.slice(lastIdx, match.index)))
        }

        // Create the red [Unidentified] marker with tooltip
        const wrapper = document.createElement('span')
        wrapper.setAttribute('data-unidentified', 'true')
        wrapper.className = 'relative inline-block group/unid cursor-help'

        const label = document.createElement('span')
        label.className = 'text-red-600 dark:text-red-400 font-medium'
        label.textContent = match[0]
        wrapper.appendChild(label)

        // Tooltip
        const tooltip = document.createElement('div')
        tooltip.className = [
          'absolute left-0 bottom-full mb-2 z-50 hidden group-hover/unid:block',
          'w-[180px] p-2.5 rounded-lg shadow-lg border',
          'bg-white dark:bg-dark-card border-[#e5e5e5] dark:border-dark-border',
        ].join(' ')
        tooltip.innerHTML = `
          <div class="flex items-center gap-1 mb-1.5">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
            <span class="text-xs font-semibold text-red-600 dark:text-red-400">${t('document_viewer:review.reviewNeeded', { defaultValue: 'Review Needed' })}</span>
          </div>
          <p class="text-xs text-foreground leading-relaxed">${t('document_viewer:review.confidenceRate', { defaultValue: '0% Confidence Rate' })}<br/>${t('document_viewer:review.cannotExtract', { defaultValue: 'We cannot extract from this chart' })}</p>
        `
        // Pointer arrow
        const arrow = document.createElement('div')
        arrow.className = 'absolute left-4 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-[#e5e5e5] dark:border-t-dark-border'
        tooltip.appendChild(arrow)
        wrapper.appendChild(tooltip)

        frag.appendChild(wrapper)
        lastIdx = match.index + match[0].length
      }

      if (lastIdx < text.length) {
        frag.appendChild(document.createTextNode(text.slice(lastIdx)))
      }

      node.parentNode?.replaceChild(frag, node)
    }
  }, [t])

  // Re-run highlighting + unidentified markers when content changes
  useEffect(() => {
    // Small delay to let markdown render first
    const t = setTimeout(() => {
      markUnidentified()
      highlightMatches()
    }, 50)
    return () => clearTimeout(t)
  }, [highlightMatches, markUnidentified, pageText])

  const navigateMatch = (direction: 'next' | 'prev') => {
    const container = contentRef.current
    if (!container || matchCount === 0) return

    const marks = container.querySelectorAll<HTMLElement>('mark[data-search-highlight]')
    // Remove active style from current
    marks.forEach(m => m.classList.remove('bg-orange-300', 'dark:bg-orange-600/60'))

    let next = direction === 'next' ? activeMatch + 1 : activeMatch - 1
    if (next > matchCount) next = 1
    if (next < 1) next = matchCount

    setActiveMatch(next)
    const mark = marks[next - 1]
    if (mark) {
      mark.classList.add('bg-orange-300', 'dark:bg-orange-600/60')
      mark.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditedContent('')
  }

  const handleSaveEdit = async () => {
    if (!docId || currentPage === undefined) {
      toast.error(t('common:document_viewer.toasts.cannot_save_missing_info'))
      return
    }

    setIsSaving(true)
    try {
      // FS-557: when an onSave override is supplied (transcript artifacts), it
      // owns persistence (full-body write + KG re-ingest). Otherwise fall back
      // to the per-page OCR text endpoint used by scanned documents.
      if (onSave) {
        await onSave(editedContent)
      } else {
        await apiService.updateDocumentPageText(docId, currentPage - 1, editedContent)
      }

      if (onContentUpdate && currentPage !== undefined) {
        onContentUpdate(editedContent, currentPage)
      }

      toast.success(t('common:document_viewer.toasts.page_text_updated'))
      setIsEditing(false)
      setEditedContent('')
    } catch (error) {
      console.error('Failed to save page text:', error)
      toast.error(t('common:document_viewer.toasts.failed_to_save'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-dark-card">
      {/* Search match counter — shown when searching */}
      {searchQuery && searchQuery.length >= 2 && matchCount > 0 && (
        <div className="flex items-center gap-2 px-4 py-1.5 border-b border-[#e5e5e5] dark:border-dark-border bg-gray-50 dark:bg-dark-muted shrink-0">
          <span className="text-xs text-muted-foreground">
            {t('document_viewer:review.matches', { active: activeMatch, total: matchCount, defaultValue: '{{active}} of {{total}} matches' })}
          </span>
          <button
            type="button"
            onClick={() => navigateMatch('prev')}
            className="size-5 flex items-center justify-center rounded hover:bg-gray-200 dark:hover:bg-dark-accent"
          >
            <ChevronUp className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => navigateMatch('next')}
            className="size-5 flex items-center justify-center rounded hover:bg-gray-200 dark:hover:bg-dark-accent"
          >
            <ChevronDown className="size-3.5" />
          </button>
        </div>
      )}

      <div className="flex-1 overflow-auto relative">
        {/* Save/Cancel when editing */}
        {isEditing && (
          <div className="sticky top-0 z-10 flex items-center justify-end gap-2 p-3 bg-white/90 dark:bg-dark-card/90 backdrop-blur border-b border-[#e5e5e5] dark:border-dark-border">
            <Button
              size="sm"
              variant="outline"
              onClick={handleCancelEdit}
              disabled={isSaving}
              className="flex items-center gap-1.5"
            >
              <X className="size-3.5" />
              {t('document_viewer:review.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              size="sm"
              onClick={handleSaveEdit}
              disabled={isSaving}
              className="flex items-center gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Save className="size-3.5" />
              {isSaving ? t('document_viewer:review.saving', { defaultValue: 'Saving...' }) : t('document_viewer:review.save', { defaultValue: 'Save' })}
            </Button>
          </div>
        )}

        {pageText ? (
          isEditing ? (
            <div className="h-full p-4">
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="w-full h-full p-4 font-mono text-sm border dark:border-dark-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-dark-card dark:text-dark-text"
                placeholder={t('document_viewer:review.editPlaceholder', { defaultValue: 'Edit the markdown content here...' })}
              />
            </div>
          ) : pageText.length > 50_000 ? (
            <div className="p-4" ref={contentRef}>
              <pre className="font-mono text-sm whitespace-pre-wrap break-words text-gray-800 dark:text-dark-text leading-relaxed">
                {pageText}
              </pre>
            </div>
          ) : (
            <div className="p-4" ref={contentRef}>
              <div className="markdown-content max-w-full" style={{ lineHeight: '1.6', wordWrap: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {pageText}
                </ReactMarkdown>
              </div>
            </div>
          )
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500 dark:text-dark-text-muted">
            {t('document_viewer:review.noContent', { defaultValue: 'No content available' })}
          </div>
        )}
      </div>
    </div>
  )
}

// Gated on the uiv2 flag (#7537): prod keeps the legacy ReviewTab until the flag
// flips; the V2 render only shows when useUIv2() is true. Addresses @amjibaly.
export function ReviewTab(props: ReviewTabProps) {
  const isV2 = useUIv2()
  return isV2 ? <ReviewTabV2 {...props} /> : <ReviewTabLegacy {...props} />
}
